import React, { useState } from 'react';
import { Box, Paper, Typography, Chip, Alert, Button, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getAdminUserInfo } from '../utils/adminUtils';
import { getCoachByUid, migrateAvailableSlotsToPerCoachFolders, deleteOldFlatAvailableSlots } from '../utils/firebaseUtils';
import { functions, httpsCallable } from '../firebase';

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const adminInfo = currentUser ? getAdminUserInfo(currentUser.uid) : null;
  const coach = currentUser ? getCoachByUid(currentUser.uid) : null;
  const isAdmin = adminInfo != null;
  const [migrating, setMigrating] = useState(false);
  const [migrationError, setMigrationError] = useState('');
  const [migrationResult, setMigrationResult] = useState(null);
  const [seedingPreview, setSeedingPreview] = useState(false);
  const [seedPreviewError, setSeedPreviewError] = useState('');
  const [seedPreviewSuccess, setSeedPreviewSuccess] = useState(false);
  const [deletingSeed, setDeletingSeed] = useState(false);
  const [deleteSeedError, setDeleteSeedError] = useState('');
  const [deleteSeedSuccess, setDeleteSeedSuccess] = useState(false);
  const [slotsMigrating, setSlotsMigrating] = useState(false);
  const [slotsMigrationResult, setSlotsMigrationResult] = useState(null);
  const [slotsDeleting, setSlotsDeleting] = useState(false);
  const [slotsDeleteResult, setSlotsDeleteResult] = useState(null);

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3, textAlign: 'center', fontWeight: 600 }}>
          {isAdmin ? 'Administratoriaus Panele' : 'Trenerio panele'}
        </Typography>

        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="h6">
            Sveiki, {isAdmin ? (adminInfo?.name || 'Administratoriau') : (coach?.name || 'Treneri')}!
          </Typography>
          <Typography variant="body2">
            {isAdmin ? 'Jūs turite pilnas administratoriaus teises.' : 'Valdote savo laisvų laikų kalendorių ir rezervacijas.'}
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Jūsų informacija:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                El. paštas:
              </Typography>
              <Typography variant="body1">
                {currentUser?.email || 'Nenurodyta'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Vartotojo ID:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {currentUser?.uid}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Rolė:
              </Typography>
              <Chip label={isAdmin ? 'Administratorius' : 'Treneris'} color="success" size="small" />
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Galimos operacijos:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2">• Peržiūrėti ir valdyti rezervacijas</Typography>
            <Typography variant="body2">• Redaguoti laisvus laikus</Typography>
            <Typography variant="body2">• Valdyti kortų informaciją</Typography>
            <Typography variant="body2">• Peržiūrėti vartotojų duomenis</Typography>
            <Typography variant="body2">• Analizuoti rezervacijų statistiką</Typography>
          </Box>
        </Box>

        <Alert severity="info">
          <Typography variant="body2">
            Naudokite navigacijos meniu, kad pasiektumėte skirtingas administratoriaus funkcijas.
          </Typography>
        </Alert>

        {isAdmin && (
          <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Backend (tenisoužsiėmimai.lt)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Administravimo puslapis kitoje domeno pusėje – prisijungimas el. paštu ir slaptažodžiu.
            </Typography>
            <Button
              variant="outlined"
              href="https://tenisoužsiėmimai.lt/#/admin"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mb: 2 }}
            >
              Atidaryti backend (tenisoužsiėmimai.lt)
            </Button>

            <Typography variant="h6" sx={{ mb: 1.5, mt: 2 }}>
              Laisvų laikų perkėlimas (available slots)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Perkelia laisvų laikų duomenis iš bendros kolekcijos į kiekvieno trenerio aplankus: availableSlots → availableSlots/trenerio_id/dates/data. Paleiskite „Perkelti“ vieną kartą, tada „Ištrinti senus“ norint pašalinti senus įrašus.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                disabled={slotsMigrating || slotsDeleting}
                onClick={async () => {
                  setSlotsMigrationResult(null);
                  setSlotsMigrating(true);
                  try {
                    const result = await migrateAvailableSlotsToPerCoachFolders();
                    setSlotsMigrationResult({ copied: result.copied, errors: result.errors });
                  } catch (err) {
                    setSlotsMigrationResult({ copied: 0, errors: [{ message: err.message }] });
                  } finally {
                    setSlotsMigrating(false);
                  }
                }}
                startIcon={slotsMigrating ? <CircularProgress size={20} /> : null}
              >
                {slotsMigrating ? 'Vykdoma...' : 'Perkelti į trenerių aplankus'}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                disabled={slotsMigrating || slotsDeleting}
                onClick={async () => {
                  setSlotsDeleteResult(null);
                  setSlotsDeleting(true);
                  try {
                    const result = await deleteOldFlatAvailableSlots();
                    setSlotsDeleteResult({ deleted: result.deleted, errors: result.errors });
                  } catch (err) {
                    setSlotsDeleteResult({ deleted: 0, errors: [{ message: err.message }] });
                  } finally {
                    setSlotsDeleting(false);
                  }
                }}
                startIcon={slotsDeleting ? <CircularProgress size={20} /> : null}
              >
                {slotsDeleting ? 'Vykdoma...' : 'Ištrinti senus įrašus'}
              </Button>
            </Box>
            {slotsMigrationResult && (
              <Alert severity={slotsMigrationResult.errors?.length ? 'warning' : 'success'} sx={{ mt: 1 }}>
                Perkelta: {slotsMigrationResult.copied} įrašų.
                {slotsMigrationResult.errors?.length > 0 && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Klaidos: {slotsMigrationResult.errors.map((e) => e.message || JSON.stringify(e)).join('; ')}
                  </Typography>
                )}
              </Alert>
            )}
            {slotsDeleteResult && (
              <Alert severity={slotsDeleteResult.errors?.length ? 'warning' : 'success'} sx={{ mt: 1 }}>
                Ištrinta: {slotsDeleteResult.deleted} senų įrašų.
                {slotsDeleteResult.errors?.length > 0 && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Klaidos: {slotsDeleteResult.errors.map((e) => e.message || JSON.stringify(e)).join('; ')}
                  </Typography>
                )}
              </Alert>
            )}

            <Typography variant="h6" sx={{ mb: 1.5, mt: 2 }}>
              Migracija (treneriai – coachId / coachName)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Prideda laukus coachId ir coachName prie senų availableSlots ir reservations dokumentų. Paleiskite vieną kartą po multi-coach atnaujinimo.
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              disabled={migrating}
              onClick={async () => {
                setMigrationError('');
                setMigrationResult(null);
                setMigrating(true);
                try {
                  const migrateCoaches = httpsCallable(functions, 'migrateCoaches');
                  const res = await migrateCoaches({});
                  const data = res.data || {};
                  setMigrationResult({
                    availableSlotsUpdated: data.availableSlotsUpdated ?? 0,
                    reservationsUpdated: data.reservationsUpdated ?? 0
                  });
                } catch (err) {
                  setMigrationError(err.message || 'Migracijos klaida.');
                } finally {
                  setMigrating(false);
                }
              }}
              startIcon={migrating ? <CircularProgress size={20} /> : null}
            >
              {migrating ? 'Vykdoma migracija...' : 'Paleisti migraciją'}
            </Button>
            {migrationResult && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Atnaujinta: availableSlots — {migrationResult.availableSlotsUpdated}, reservations — {migrationResult.reservationsUpdated}.
              </Alert>
            )}
            {migrationError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {migrationError}
              </Alert>
            )}

            <Typography variant="h6" sx={{ mb: 1.5, mt: 2 }}>
              Seed duomenys (preview@tenisouzsiemimai.lt)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Užpildo fiktyviomis rezervacijomis ir laisvais laikais tik trenerio „Peržiūra“ (preview) paskyrai. Realūs Elvis ir kitų trenerių duomenys niekada neįtakojami. Seed rodosi tik preview kalendoriuje, ne admin rezervacijų žurnale.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="secondary"
                disabled={seedingPreview || deletingSeed}
                onClick={async () => {
                  setSeedPreviewError('');
                  setSeedPreviewSuccess(false);
                  setDeleteSeedSuccess(false);
                  setSeedingPreview(true);
                  try {
                    const seedPreview = httpsCallable(functions, 'seedPreviewCoachData');
                    await seedPreview({});
                    setSeedPreviewSuccess(true);
                  } catch (err) {
                    setSeedPreviewError(err.message || 'Seed klaida.');
                  } finally {
                    setSeedingPreview(false);
                  }
                }}
                startIcon={seedingPreview ? <CircularProgress size={20} /> : null}
              >
                {seedingPreview ? 'Vykdoma...' : 'Seed duomenys'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                disabled={deletingSeed || seedingPreview}
                onClick={async () => {
                  setDeleteSeedError('');
                  setDeleteSeedSuccess(false);
                  setSeedPreviewSuccess(false);
                  setDeletingSeed(true);
                  try {
                    const deleteSeed = httpsCallable(functions, 'deleteSeedData');
                    const res = await deleteSeed({});
                    setDeleteSeedSuccess(res?.data?.message || true);
                  } catch (err) {
                    setDeleteSeedError(err.message || 'Ištrynimo klaida.');
                  } finally {
                    setDeletingSeed(false);
                  }
                }}
                startIcon={deletingSeed ? <CircularProgress size={20} /> : null}
              >
                {deletingSeed ? 'Trinama...' : 'Ištrinti seed duomenis'}
              </Button>
            </Box>
            {seedPreviewSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Seed duomenys sėkmingai užpildyti.
              </Alert>
            )}
            {seedPreviewError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {seedPreviewError}
              </Alert>
            )}
            {deleteSeedSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {typeof deleteSeedSuccess === 'string' ? deleteSeedSuccess : 'Seed duomenys ištrinti.'}
              </Alert>
            )}
            {deleteSeedError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {deleteSeedError}
              </Alert>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AdminDashboard; 